import { useState, type FormEvent } from "react";
import { postJson } from "../lib/api";

interface FormState {
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const INITIAL: FormState = {
  fullName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

export default function ContactForm() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<
    { kind: "idle" } | { kind: "success" } | { kind: "error"; msg: string }
  >({ kind: "idle" });

  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ kind: "idle" });
    const res = await postJson("/api/contact", { ...form });
    setSubmitting(false);
    if (res.ok) {
      setStatus({ kind: "success" });
      setForm(INITIAL);
    } else {
      setStatus({ kind: "error", msg: res.error });
    }
  };

  if (status.kind === "success") {
    return (
      <div className="surface animate-fadeUp">
        <span className="pill pill-mint">Message received</span>
        <h3 className="text-2xl font-display mt-3">Thank you.</h3>
        <p className="text-ink-500 mt-2">
          We'll reply to the email or phone you provided.
        </p>
        <button
          type="button"
          onClick={() => setStatus({ kind: "idle" })}
          className="btn-ghost mt-6"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="surface space-y-5" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="c-fullName">Full name *</label>
          <input
            id="c-fullName"
            className="field-input"
            required
            value={form.fullName}
            onChange={(e) => upd("fullName", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="c-email">Email *</label>
          <input
            id="c-email"
            type="email"
            className="field-input"
            required
            value={form.email}
            onChange={(e) => upd("email", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="c-phone">Phone</label>
          <input
            id="c-phone"
            type="tel"
            className="field-input"
            value={form.phone}
            onChange={(e) => upd("phone", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="c-subject">Subject *</label>
          <input
            id="c-subject"
            className="field-input"
            required
            value={form.subject}
            onChange={(e) => upd("subject", e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="field-label" htmlFor="c-message">Message *</label>
        <textarea
          id="c-message"
          className="field-textarea"
          required
          value={form.message}
          onChange={(e) => upd("message", e.target.value)}
        />
      </div>

      {status.kind === "error" && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-glow">
          {status.msg}
        </div>
      )}

      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Sending…" : "Send message →"}
      </button>
    </form>
  );
}
